import { useGlobalModal } from "@/hooks/useGlobalModal";

import type {
  DashboardDataCell,
  DashboardLatestTreatmentValue,
  DashboardPolicyLimitsValue,
} from "@/lib/types/MatterDashboardTable";

function formatDecimalMoney(value: string | null): string {
  if (value === null) return "—";
  const match = /^(-?)(\d+)(\.\d+)?$/.exec(value);
  if (!match) return value;
  const [, sign, integer, fraction = ""] = match;
  return `${sign}$${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${fraction}`;
}

function formatCalendarDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
}

function isPolicyLimitsValue(
  value: DashboardDataCell["value"],
): value is DashboardPolicyLimitsValue {
  return !!value && "policies" in value && Array.isArray(value.policies);
}

function isLatestTreatmentValue(
  value: DashboardDataCell["value"],
): value is DashboardLatestTreatmentValue {
  return !!value && "date" in value && typeof value.date === "string";
}

export function DashboardMatterDataCell({
  cell,
  matterId,
}: {
  cell: DashboardDataCell | null | undefined;
  matterId: string;
}) {
  const { openTreatmentAppointmentModal } = useGlobalModal();
  if (!cell || cell.state === "empty") {
    return <span className="text-muted-foreground">—</span>;
  }
  if (cell.state === "unavailable") {
    return <span className="text-muted-foreground text-xs">Unavailable</span>;
  }
  if (cell.valueKind === "policyLimits" && isPolicyLimitsValue(cell.value)) {
    const value = cell.value;
    return (
      <div className="flex flex-col gap-2 text-xs">
        {value.policies.map((policy) => (
          <div key={policy.insuranceId} className="min-w-0">
            <span
              className="block truncate font-medium"
              title={policy.insurer?.name ?? undefined}
            >
              {policy.insurer?.name || "Unnamed insurer"}
            </span>
            <span className="text-muted-foreground block whitespace-nowrap">
              {formatDecimalMoney(policy.perPerson)} /{" "}
              {formatDecimalMoney(policy.perIncident)}
            </span>
            {policy.limitsVerified !== null && (
              <span className="text-muted-foreground block">
                Limits {policy.limitsVerified ? "verified" : "unverified"}
              </span>
            )}
          </div>
        ))}
        {value.hasMore && (
          <span className="text-muted-foreground">More policies</span>
        )}
      </div>
    );
  }
  if (
    cell.valueKind === "latestTreatment" &&
    isLatestTreatmentValue(cell.value)
  ) {
    const value = cell.value;
    return (
      <div className="min-w-0 text-xs">
        <button
          type="button"
          className="text-primary block cursor-pointer text-left font-medium hover:underline"
          onClick={() =>
            openTreatmentAppointmentModal({
              matterId,
              appointmentId: value.appointmentId,
              treatmentId: value.treatmentId,
              editing: true,
              initialTab: "details",
            })
          }
        >
          {formatCalendarDate(value.date)}
        </button>
        {value.provider?.name && (
          <span
            className="text-muted-foreground block truncate"
            title={value.provider.name}
          >
            {value.provider.name}
          </span>
        )}
      </div>
    );
  }
  return (
    <span className="text-muted-foreground text-xs">Unsupported value</span>
  );
}
